// The Revenue tab's two trend charts, served from the rollup payload.
//
// They were the last surface still reading raw rows, and the only one with no
// rollup counterpart -- so when the rollup path became the default they were
// handed useSchedulingData's empty array and drew nothing. No error: an empty
// array is a valid input that produces an empty chart. Every other figure on the
// tab switched sources correctly, which is why the cards and the drill table
// looked fine either side of it.
//
// Bucketing is buildTrendFromRollup, the same function the Utilization trend
// uses, rather than a revenue-specific copy. The bucketing rules are the
// dangerous part -- the Thursday offset that decides which month a trailing
// week belongs to, the 7-bit day mask, the calendar-month straddle -- and a
// second implementation of them would be a second chance to get them wrong
// differently. Only the arithmetic on top differs, and that lives here.
//
// Like useRollupTrend, this MUST be given a payload fetched at the granularity
// it is asked to bucket; the two charts are grouped independently, so it is
// called twice with two payloads. See useRollupTrend for why one payload cannot
// serve both.

import { useMemo } from "react";
import type { FilterState } from "@/types/filters";
import { buildTrendFromRollup } from "@/lib/rollupTrend";
import { getBucketLabel, getPeriodFamily, type DateRange } from "@/lib/periods";
import type { RollupPayload } from "@/hooks/useRollupData";
import type { RevenueTrendData, RevenueTrendPoint } from "@/hooks/useRevenueTrendData";
import { splitRange } from "@/lib/rollupSplit";
import type { TrendBucketTotals } from "@/lib/trendMetrics";

const EMPTY_SCOPE = {
  countryCode: null,
  divisionName: null,
  groupName: null,
  locationId: null,
  resourceId: null,
};

function toPoint(
  t: TrendBucketTotals,
  granularity: FilterState["revGranularity"]
): RevenueTrendPoint {
  return {
    bucketKey: t.bucketKey,
    label: getBucketLabel(t.bucketKey, granularity),
    totalMedRev: t.totalMedRev,
    dvmDays: t.dvmDays,
    medRevPerDvmDay: t.dvmDays > 0 ? t.totalMedRev / t.dvmDays : 0,
    medRevPerSeenHour: t.allSeenMin > 0 ? t.totalMedRev / (t.allSeenMin / 60) : 0,
    medRevPerSchedHour: t.totalScheduledMin > 0 ? t.totalMedRev / (t.totalScheduledMin / 60) : 0,
    medRevPerAppt: t.totalCompleted > 0 ? t.totalMedRev / t.totalCompleted : 0,
  };
}

export function useRollupRevenueTrend(
  payload: RollupPayload | null,
  filters: FilterState,
  /** MUST be the granularity `payload` was fetched at. */
  granularity: FilterState["revGranularity"]
): RevenueTrendData | null {
  const { customStart, customEnd, period, scope, excludeZeroApptDays, drillLevel } = filters;
  return useMemo(() => {
    if (!payload) return null;

    const family = getPeriodFamily(period);
    const range: DateRange = { startDate: customStart, endDate: customEnd };
    const split = splitRange(range, granularity, family);
    const weeks = new Set(split.wholeWeeks);
    const days = new Set(split.edgeDays);
    const part = {
      siteWeeks: payload.siteWeeks.filter((r) => weeks.has(String(r.week_start))),
      dvmWeeks: payload.dvmWeeks.filter((r) => weeks.has(String(r.week_start))),
      edgeRows: payload.edgeRows.filter((r) => days.has(r.schedule_date)),
    };

    const common = {
      ...part,
      range,
      granularity,
      family,
      // The charts show revenue, never an appointment-type split, so no type
      // filter reaches the bucket sums.
      appointmentTypes: [],
      excludeZeroApptDays,
    };

    const scoped = buildTrendFromRollup({
      ...common,
      scope,
      measuresFromDvmWeeks:
        payload.wideDvmRows && (drillLevel === "dvm" || scope.resourceId !== null),
    });

    // The NVA benchmark is the same buckets read with no scope at all. It can be
    // taken from this payload because the site-week and edge rows in it are
    // UNSCOPED -- fetched that way precisely so the benchmark exists (see
    // rollupFetch.ts).
    //
    // measuresFromDvmWeeks is false here and that is not an oversight. The WIDE
    // doctor rows ARE scoped, so reading an unscoped total from them would sum
    // only the user's selection and report it as the company -- the same
    // measure-source mistake that once made the NVA benchmark read ~50x low.
    const nvaByKey = new Map(
      buildTrendFromRollup({ ...common, scope: EMPTY_SCOPE, measuresFromDvmWeeks: false }).map(
        (t) => [t.bucketKey, toPoint(t, granularity)]
      )
    );

    const points = scoped.map((t) => toPoint(t, granularity));
    return {
      points,
      nvaTotalMedRev: points.map((p) => nvaByKey.get(p.bucketKey)?.totalMedRev ?? null),
      nvaMedRevPerDvmDay: points.map((p) => nvaByKey.get(p.bucketKey)?.medRevPerDvmDay ?? null),
    };
  }, [
    payload,
    granularity,
    customStart,
    customEnd,
    period,
    scope,
    excludeZeroApptDays,
    drillLevel,
  ]);
}
